import type { EnumProps } from "@/components/EnumBadge.tsx";
import type { Translatable } from "@/modules/l10n/getTransText.ts";
import { Trans } from "@lingui/react/macro";
import type {
    BillingAccountStatus,
    BillingAccountStatusActorType,
    BillingAccountStatusEventType,
    RestrictionLevel,
} from "@warrenio/api-spec/spec.oats.gen";
import { getOwn } from "@warrenio/utils/collections/getOwnProperty";
import type { ReactNode } from "react";

export const statusHistoryTitle: Translatable = (
    <Trans comment="Short of: billing account status history">Status history</Trans>
);

export const noHistoryText: Translatable = (
    <Trans comment="Shown when the billing account status history is empty">No history available.</Trans>
);

export const restrictionLevelLabels: Record<RestrictionLevel, EnumProps> = {
    CLEAR: {
        label: <Trans comment="Billing account restriction level | POS: Adjective">Clear</Trans>,
        description: <Trans>No restrictions</Trans>,
        color: "success",
    },
    LIMITED: {
        label: <Trans comment="Billing account restriction level">Limited</Trans>,
        description: <Trans>Resource allocation is limited</Trans>,
        color: "primary",
    },
    FROZEN: {
        label: <Trans comment="Billing account restriction level">Frozen</Trans>,
        description: <Trans>Resource allocation is forbidden. Existing resources are stopped.</Trans>,
        color: "error",
    },
    TERMINATED: {
        label: <Trans comment="Billing account restriction level">Terminated</Trans>,
        description: <Trans>Resource allocation is forbidden. Existing resources are deleted.</Trans>,
        color: "error",
    },
};

export const accountStatusLabels: Record<BillingAccountStatus, EnumProps> = {
    ACTIVE: { label: <Trans comment="Billing account status">Active</Trans>, color: "success" },
    SUSPENDED: { label: <Trans comment="Billing account status">Suspended</Trans>, color: "warning" },
    PENDING_VALIDATION: {
        label: <Trans comment="Billing account status">Pending validation</Trans>,
        color: "warning",
    },
};

export const eventTypeLabels: Record<BillingAccountStatusEventType, Translatable> = {
    RESTRICTION_LEVEL_CHANGED: <Trans>Restriction level changed</Trans>,
    BALANCE_BECAME_NEGATIVE: <Trans>Balance became negative</Trans>,
    BALANCE_BECAME_POSITIVE: <Trans>Balance became positive</Trans>,
    SUSPENDED: <Trans>Billing account suspended</Trans>,
    UNSUSPENDED: <Trans>Billing account reactivated</Trans>,
    SUSPEND_REASON_CHANGED: <Trans>Suspension reason changed</Trans>,
};

export const actorTypeLabels: Record<BillingAccountStatusActorType, Translatable> = {
    USER: <Trans comment="Who changed the billing account status">User</Trans>,
    ADMINISTRATOR: <Trans comment="Who changed the billing account status">Administrator</Trans>,
    SYSTEM: <Trans comment="Who changed the billing account status">System</Trans>,
};

const administratorActionLabel: Translatable = (
    <Trans comment="What caused the billing account status change">Administrator action</Trans>
);

/** Labels for the `trigger` field (the API can also return values that are not listed here) */
const triggerLabels: Record<string, Translatable> = {
    system: <Trans comment="What caused the billing account status change">System</Trans>,
    charging_subtotal: <Trans comment="What caused the billing account status change">Usage charge</Trans>,
    topup: <Trans comment="What caused the billing account status change">Top-up</Trans>,
    admin_credit: <Trans comment="What caused the billing account status change">Credit adjustment</Trans>,
    payment_method: <Trans comment="What caused the billing account status change">Payment method</Trans>,
    paying_by_invoice: <Trans>Paying by invoice</Trans>,
    invoice: <Trans comment="What caused the billing account status change">Invoice</Trans>,
    invoice_payment: <Trans comment="What caused the billing account status change">Invoice payment</Trans>,
    kyc: <Trans comment="What caused the billing account status change">Billing account validation</Trans>,
    account_create: <Trans>Billing account created</Trans>,
    account_update: <Trans>Billing account updated</Trans>,
    account_delete: <Trans>Billing account deleted</Trans>,
    admin_suspend: administratorActionLabel,
    admin_unsuspend: administratorActionLabel,
    admin_suspend_reason: administratorActionLabel,
    admin_open_level_override: administratorActionLabel,
    admin_account_settings: administratorActionLabel,
};

export function getTriggerLabel(trigger: string): ReactNode {
    return getOwn(triggerLabels, trigger) ?? trigger;
}
